import React, { useState } from "react";
import { PartnerFormShell, Row, Field, Pill } from "./PartnerFormShell";
import { Boxes, Zap, PackageCheck } from "lucide-react";

const CATEGORIES = [
  { code: "paper",      label: "Paper" },
  { code: "board",      label: "Boards (duplex, FBB, SBS, kraft)" },
  { code: "flexo_film", label: "Flexo film / laminates" },
  { code: "ink",        label: "Inks & coatings" },
  { code: "plate",      label: "Plates & chemicals" },
  { code: "adhesive",   label: "Adhesives" },
  { code: "other",      label: "Other" },
];

export default function PartnerMaterial() {
  const [extra, setExtra] = useState({
    categories: [], brands_carried: "",
    moq: "", lead_time_days: "",
    pricing_model: "list", warehouse_pincodes: "",
    delivers_pan_india: false,
    catalogue_url: "",
  });

  const toggleCat = (c) => setExtra(e => ({
    ...e,
    categories: e.categories.includes(c) ? e.categories.filter(x => x !== c) : [...e.categories, c],
  }));
  const setField = (e) => {
    const { name, value, type, checked } = e.target;
    setExtra(x => ({ ...x, [name]: type === "checkbox" ? checked : value }));
  };

  return (
    <PartnerFormShell
      partnerType="material"
      testIdPrefix="partner-material"
      accentHue="#10B981"
      title="Join as a Material Supplier"
      lede="Buyers on our platform need paper, boards, film, inks, plates and adhesives — fast. Join the RFQ layer: a buyer posts a sealed request, you quote, the fastest/cheapest wins. No listing fees."
      benefits={[
        { icon: <PackageCheck size={16}/>, title: "Order-level RFQs",   body: "Printers post requirements tied to a real job — not vague 'send me a quote' leads." },
        { icon: <Zap size={16}/>,          title: "Sealed sub-auctions", body: "Your bid stays private. Only you and the printer see it until they award." },
        { icon: <Boxes size={16}/>,        title: "Pan-India demand",    body: "340+ brands run through our printers; your reach grows with every onboarded press." },
      ]}
      extraState={extra}
      validateExtra={() => extra.categories.length === 0 ? "Please tick at least one material category." : null}
      extraFields={
        <>
          <div className="pf-group">
            <label className="pf-label">Material categories you supply *</label>
            <p className="pf-help">We match RFQs to suppliers by category — pick everything you stock regularly.</p>
            <div className="pf-pillrow">
              {CATEGORIES.map(c => (
                <Pill key={c.code} label={c.label}
                      active={extra.categories.includes(c.code)}
                      onClick={() => toggleCat(c.code)}
                      testid={`partner-material-cat-${c.code}`}/>
              ))}
            </div>
          </div>
          <Row>
            <Field label="Brands carried (comma-separated)" name="brands_carried"
                   value={extra.brands_carried} onChange={setField}
                   placeholder="e.g. ITC, JK Paper, Sun Chemical, Henkel"
                   maxLength={300} testid="partner-material-brands"/>
            <Field label="Minimum order quantity" name="moq" value={extra.moq}
                   onChange={setField} placeholder="e.g. 1000 kg / 100 reams"
                   maxLength={60} testid="partner-material-moq"/>
          </Row>
          <Row>
            <Field label="Typical lead time (days)" name="lead_time_days" value={extra.lead_time_days}
                   onChange={setField} placeholder="e.g. 3" type="number" maxLength={3}
                   testid="partner-material-lead"/>
            <label className="pf-field">
              <span>Pricing model</span>
              <select name="pricing_model" value={extra.pricing_model} onChange={setField}
                      data-testid="partner-material-pricing">
                <option value="list">List price</option>
                <option value="negotiated">Negotiated per order</option>
                <option value="contract">Contract-based</option>
              </select>
            </label>
          </Row>
          <Row>
            <Field label="Warehouse pincodes (comma-separated)" name="warehouse_pincodes"
                   value={extra.warehouse_pincodes} onChange={setField}
                   placeholder="411001, 400001, 110001" maxLength={200}
                   testid="partner-material-warehouses"/>
            <Field label="Catalogue PDF / URL" name="catalogue_url" value={extra.catalogue_url}
                   onChange={setField} placeholder="https://..." maxLength={400}
                   testid="partner-material-catalogue"/>
          </Row>
          <label className="pf-check" style={{marginTop:12}}>
            <input type="checkbox" name="delivers_pan_india"
                   checked={extra.delivers_pan_india} onChange={setField}
                   data-testid="partner-material-panindia"/>
            <span>We deliver pan-India (via our logistics partners)</span>
          </label>
        </>
      }
    />
  );
}
